/**
 * As fotos do veículo, na ficha do veículo.
 *
 * **É a foto que a busca mostra.** Cada uma vale para uma faixa de anos: 0 a 0
 * para todos, início e fim para o intervalo, só o início para "daquele ano em
 * diante". O arquivo sobe pela ApiSistema, que o repassa à ApiConteudo — a tela
 * não sabe de pasta nenhuma.
 *
 * **A miniatura vem pela rota da busca.** Se ela não aparece, o site também não
 * mostra a foto, e a ficha diz isso em vez de desenhar um quadro vazio.
 *
 * **Avisa quando uma foto perde para outra.** Nos anos em comum a busca pega a de
 * menor ano inicial, e "todos os anos" ganha de qualquer faixa — uma foto nova
 * de 2015 a 2020 num veículo que já tem uma para todos os anos nunca aparece, e
 * sem o aviso ninguém entenderia por quê.
 */

import { useEffect, useState } from "react"
import {
  FORMATOS_DE_FOTO, fotosEncobertas, problemaDaFaixa, textoDaFaixa, urlDaImagemDoVeiculo,
  usarAlterarImagemDoVeiculo, usarEnviarImagemDoVeiculo, usarExcluirImagemDoVeiculo,
  usarImagensDoVeiculo, type ImagemDoVeiculo,
} from "@/api/imagensDoVeiculo"
import { ErroDaApi, ErroDeTrava } from "@/api/erros"

type Avisar = (m: string | null) => void

function mensagemDe(e: unknown, padrao: string) {
  return e instanceof ErroDeTrava || e instanceof ErroDaApi ? e.message : padrao
}

/** "" vira 0 — o "sem limite" da faixa. */
const ano = (v: string) => (v.trim() === "" ? 0 : Number(v))

export function ImagensDoVeiculo({ veiculoId, nomeDoVeiculo, aoAvisar }: {
  veiculoId: number
  /** Vai para a trava: em desenvolvimento só a foto de um veículo ZZTESTE grava. */
  nomeDoVeiculo: string
  aoAvisar: Avisar
}) {
  const consulta = usarImagensDoVeiculo(veiculoId)
  const imagens = consulta.data ?? []
  const encobertas = fotosEncobertas(imagens)

  return (
    <div style={{ marginTop: 14, paddingTop: 12, borderTop: "1px solid var(--line2)" }}>
      <h3 style={titulo}>Fotos</h3>
      <p style={{ margin: "0 0 7px", fontSize: 10.5, color: "var(--txt3)", lineHeight: 1.5 }}>
        A foto que a busca mostra para este veículo, uma por faixa de anos. Deixe os dois anos
        vazios para valer em <b>todos</b>; só o inicial, para valer <b>daquele ano em diante</b>.
      </p>

      {consulta.isPending ? (
        <p style={vazio}>Carregando…</p>
      ) : consulta.error ? (
        <p style={{ ...vazio, color: "var(--erro)" }}>{mensagemDe(consulta.error, "Não foi possível ler.")}</p>
      ) : imagens.length === 0 ? (
        <p style={vazio}>Nenhuma foto: a busca mostra o veículo sem imagem.</p>
      ) : (
        <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 8 }}>
          {imagens.map((img) => {
            const quem = encobertas.get(img.id)
            const vencedora = quem === undefined ? undefined : imagens.find((i) => i.id === quem)
            return (
              <Foto
                key={img.id}
                veiculoId={veiculoId}
                nomeDoVeiculo={nomeDoVeiculo}
                imagem={img}
                encobertaPor={vencedora}
                aoAvisar={aoAvisar}
              />
            )
          })}
        </ul>
      )}

      <NovaFoto veiculoId={veiculoId} nomeDoVeiculo={nomeDoVeiculo} aoAvisar={aoAvisar} />
    </div>
  )
}

function Foto({ veiculoId, nomeDoVeiculo, imagem, encobertaPor, aoAvisar }: {
  veiculoId: number
  nomeDoVeiculo: string
  imagem: ImagemDoVeiculo
  encobertaPor?: ImagemDoVeiculo
  aoAvisar: Avisar
}) {
  const [editando, setEditando] = useState(false)
  const [confirmando, setConfirmando] = useState(false)
  // Sobe a cada troca de arquivo: o nome é VeiculoImagem{id}, e com a mesma extensão
  // nada no registro muda — só o conteúdo atrás do mesmo endereço.
  const [versao, setVersao] = useState(0)
  const excluir = usarExcluirImagemDoVeiculo(veiculoId, nomeDoVeiculo)
  const faixa = textoDaFaixa(imagem.anoIni, imagem.anoFim)

  return (
    <li style={{ border: "1px solid var(--line2)", borderRadius: 7, padding: 8 }}>
      <div style={{ display: "flex", gap: 9, alignItems: "flex-start" }}>
        <Miniatura veiculoId={veiculoId} imagem={imagem} versao={versao} />
        <div style={{ minWidth: 0, flex: 1 }}>
          <b style={{ fontSize: 12 }}>{faixa}</b>
          <div className="mono" title={imagem.imagem} style={{
            fontSize: 10, color: "var(--txt3)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          }}>
            {imagem.imagem}
          </div>
          {encobertaPor && (
            <p style={{ margin: "4px 0 0", fontSize: 10.5, lineHeight: 1.4, color: "var(--txt2)" }}>
              Nos anos em comum, a busca mostra a de{" "}
              <b>{textoDaFaixa(encobertaPor.anoIni, encobertaPor.anoFim).toLowerCase()}</b>.
            </p>
          )}
          {!editando && (
            <div style={{ display: "flex", gap: 5, marginTop: 6, flexWrap: "wrap" }}>
              <button style={botao} onClick={() => { setEditando(true); setConfirmando(false) }}>
                Alterar
              </button>
              {confirmando ? (
                <>
                  <button
                    style={{ ...botao, borderColor: "var(--erro)", color: "var(--erro)" }}
                    disabled={excluir.isPending}
                    onClick={() => {
                      aoAvisar(null)
                      excluir.mutate(imagem.id, {
                        onSuccess: () => aoAvisar(`Foto de ${faixa.toLowerCase()} excluída.`),
                        onError: (e) => { setConfirmando(false); aoAvisar(mensagemDe(e, "Não foi possível excluir.")) },
                      })
                    }}
                  >
                    {excluir.isPending ? "Excluindo…" : "Confirmar exclusão"}
                  </button>
                  <button style={botao} onClick={() => setConfirmando(false)}>Cancelar</button>
                </>
              ) : (
                <button style={botao} onClick={() => setConfirmando(true)}>Excluir</button>
              )}
            </div>
          )}
        </div>
      </div>

      {editando && (
        <AlterarFoto
          veiculoId={veiculoId}
          nomeDoVeiculo={nomeDoVeiculo}
          imagem={imagem}
          aoFechar={() => setEditando(false)}
          aoTrocarArquivo={() => setVersao((v) => v + 1)}
          aoAvisar={aoAvisar}
        />
      )}
    </li>
  )
}

/** A foto baixada com o token, como URL de blob — devolvida quando sai da tela. */
function Miniatura({ veiculoId, imagem, versao }: {
  veiculoId: number
  imagem: ImagemDoVeiculo
  /** Muda quando o arquivo foi trocado — ver `Foto`. */
  versao: number
}) {
  const [url, setUrl] = useState<string | null>(null)
  const [falhou, setFalhou] = useState(false)

  // O nome sozinho não basta: trocar a foto pela mesma extensão mantém id e nome.
  // Quem avisa da troca é a `versao`; o nome continua para a troca de extensão.
  useEffect(() => {
    let vivo = true
    let criada: string | null = null
    setUrl(null)
    setFalhou(false)
    urlDaImagemDoVeiculo(veiculoId, imagem.id, 160)
      .then((u) => { criada = u; if (vivo) setUrl(u); else URL.revokeObjectURL(u) })
      .catch(() => { if (vivo) setFalhou(true) })
    return () => { vivo = false; if (criada) URL.revokeObjectURL(criada) }
  }, [veiculoId, imagem.id, imagem.imagem, versao])

  return (
    <div
      title={falhou ? "O arquivo não está onde a busca procura — o site também não mostra esta foto." : undefined}
      style={{
        flex: "0 0 auto", width: 80, height: 60, borderRadius: 5, overflow: "hidden",
        border: `1px solid ${falhou ? "var(--erro)" : "var(--line2)"}`, background: "var(--surface2)",
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 9.5, color: falhou ? "var(--erro)" : "var(--txt3)", textAlign: "center", lineHeight: 1.3,
      }}
    >
      {url ? (
        <img src={url} alt={`Foto de ${textoDaFaixa(imagem.anoIni, imagem.anoFim).toLowerCase()}`}
          style={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }} />
      ) : falhou ? "Arquivo não encontrado" : "…"}
    </div>
  )
}

function AlterarFoto({ veiculoId, nomeDoVeiculo, imagem, aoFechar, aoTrocarArquivo, aoAvisar }: {
  veiculoId: number
  nomeDoVeiculo: string
  imagem: ImagemDoVeiculo
  aoFechar: () => void
  aoTrocarArquivo: () => void
  aoAvisar: Avisar
}) {
  const [anoIni, setAnoIni] = useState(imagem.anoIni ? String(imagem.anoIni) : "")
  const [anoFim, setAnoFim] = useState(imagem.anoFim ? String(imagem.anoFim) : "")
  const [arquivo, setArquivo] = useState<File | null>(null)
  const alterar = usarAlterarImagemDoVeiculo(veiculoId, nomeDoVeiculo)
  const problema = problemaDaFaixa(ano(anoIni), ano(anoFim))

  return (
    <div style={{ marginTop: 8, paddingTop: 8, borderTop: "1px dashed var(--line2)" }}>
      <CamposDaFaixa
        anoIni={anoIni} anoFim={anoFim} aoMudarIni={setAnoIni} aoMudarFim={setAnoFim} problema={problema}
      />
      <label style={rotulo}>
        Trocar a foto (opcional)
        <input type="file" accept={FORMATOS_DE_FOTO} onChange={(e) => setArquivo(e.target.files?.[0] ?? null)}
          style={{ fontSize: 11, marginTop: 3 }} />
      </label>
      <div style={{ display: "flex", gap: 5, marginTop: 8 }}>
        <button
          style={botaoDestaque}
          disabled={problema !== null || alterar.isPending}
          onClick={() => {
            aoAvisar(null)
            alterar.mutate({ id: imagem.id, anoIni: ano(anoIni), anoFim: ano(anoFim), arquivo }, {
              onSuccess: (r) => {
                if (arquivo) aoTrocarArquivo()
                aoFechar()
                aoAvisar(`Foto de ${textoDaFaixa(r.anoIni, r.anoFim).toLowerCase()} gravada.`)
              },
              onError: (e) => aoAvisar(mensagemDe(e, "Não foi possível gravar.")),
            })
          }}
        >
          {alterar.isPending ? "Gravando…" : "Gravar"}
        </button>
        <button style={botao} onClick={aoFechar}>Cancelar</button>
      </div>
    </div>
  )
}

function NovaFoto({ veiculoId, nomeDoVeiculo, aoAvisar }: { veiculoId: number; nomeDoVeiculo: string; aoAvisar: Avisar }) {
  const [anoIni, setAnoIni] = useState("")
  const [anoFim, setAnoFim] = useState("")
  const [arquivo, setArquivo] = useState<File | null>(null)
  // Trocar o `key` do input é o jeito de limpar um <input type="file">.
  const [geracao, setGeracao] = useState(0)
  const enviar = usarEnviarImagemDoVeiculo(veiculoId, nomeDoVeiculo)
  const problema = problemaDaFaixa(ano(anoIni), ano(anoFim))

  return (
    <div style={{ marginTop: 10, padding: 8, borderRadius: 7, background: "var(--surface2)" }}>
      <b style={{ fontSize: 11.5 }}>Incluir foto</b>
      <label style={{ ...rotulo, marginTop: 6 }}>
        Arquivo da foto (JPEG, PNG ou WebP)
        <input key={geracao} type="file" accept={FORMATOS_DE_FOTO}
          onChange={(e) => setArquivo(e.target.files?.[0] ?? null)} style={{ fontSize: 11, marginTop: 3 }} />
      </label>
      <CamposDaFaixa
        anoIni={anoIni} anoFim={anoFim} aoMudarIni={setAnoIni} aoMudarFim={setAnoFim} problema={problema}
      />
      <button
        style={{ ...botaoDestaque, marginTop: 8 }}
        disabled={!arquivo || problema !== null || enviar.isPending}
        onClick={() => {
          if (!arquivo) return
          aoAvisar(null)
          enviar.mutate({ anoIni: ano(anoIni), anoFim: ano(anoFim), arquivo }, {
            onSuccess: (r) => {
              setAnoIni(""); setAnoFim(""); setArquivo(null); setGeracao((g) => g + 1)
              aoAvisar(`Foto de ${textoDaFaixa(r.anoIni, r.anoFim).toLowerCase()} enviada.`)
            },
            onError: (e) => aoAvisar(mensagemDe(e, "Não foi possível enviar a foto.")),
          })
        }}
      >
        {enviar.isPending ? "Enviando…" : "Enviar foto"}
      </button>
    </div>
  )
}

function CamposDaFaixa({ anoIni, anoFim, aoMudarIni, aoMudarFim, problema }: {
  anoIni: string
  anoFim: string
  aoMudarIni: (v: string) => void
  aoMudarFim: (v: string) => void
  problema: string | null
}) {
  return (
    <>
      <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
        <label style={rotulo}>
          Ano inicial
          <input type="number" inputMode="numeric" value={anoIni} placeholder="todos"
            onChange={(e) => aoMudarIni(e.target.value)} style={campoDeAno} />
        </label>
        <label style={rotulo}>
          Ano final
          <input type="number" inputMode="numeric" value={anoFim} placeholder="sem fim"
            onChange={(e) => aoMudarFim(e.target.value)} style={campoDeAno} />
        </label>
      </div>
      {problema ? (
        <p role="alert" style={{ margin: "5px 0 0", fontSize: 10.5, color: "var(--erro)" }}>{problema}</p>
      ) : (
        <p style={{ margin: "5px 0 0", fontSize: 10.5, color: "var(--txt3)" }}>
          Vale para: <b>{textoDaFaixa(ano(anoIni), ano(anoFim)).toLowerCase()}</b>.
        </p>
      )}
    </>
  )
}

const titulo: React.CSSProperties = {
  margin: "0 0 5px", fontSize: 10, fontWeight: 600, letterSpacing: ".06em",
  textTransform: "uppercase", color: "var(--acao)",
}

const vazio: React.CSSProperties = { margin: 0, fontSize: 11.5, color: "var(--txt3)" }

const rotulo: React.CSSProperties = {
  display: "flex", flexDirection: "column", fontSize: 10.5, color: "var(--txt2)",
}

const campoDeAno: React.CSSProperties = {
  width: 90, marginTop: 3, padding: "3px 6px", fontSize: 12,
  border: "1px solid var(--line)", borderRadius: 5, background: "var(--surface)", color: "var(--txt)",
}

const botao: React.CSSProperties = {
  border: "1px solid var(--line)", background: "var(--surface)", color: "var(--txt2)",
  borderRadius: 6, padding: "3px 10px", fontSize: 11.5, cursor: "pointer",
}

const botaoDestaque: React.CSSProperties = {
  ...botao, border: "1px solid var(--acao)", background: "var(--acao)", color: "#fff",
}
